import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  strapiCreateDynamicEntity,
  strapiDeleteDynamicEntity,
  strapiListDynamicEntity,
  strapiListDynamicEntityPage,
  strapiUpdateDynamicEntity,
} from '../../api/strapiDynamicEntity';
import type { MasterCrudRegistryEntry } from '../../masterData/masterCrudRegistry';
import { MASTER_CRUD_REGISTRY } from '../../masterData/masterCrudRegistry';
import { queryKeys } from '../queryKeys';

function masterEntityContainsFiltersKey(containsFilters?: Partial<Record<string, string>>): string {
  if (!containsFilters) return '';
  const trimmed: Record<string, string> = {};
  for (const [k, v] of Object.entries(containsFilters)) {
    const t = String(v ?? '').trim();
    if (t) trimmed[k] = t;
  }
  const keys = Object.keys(trimmed).sort();
  if (keys.length === 0) return '';
  return keys.map((k) => `${k}=${trimmed[k]}`).join('&');
}

export function useMasterEntityListQuery(
  jwt: string | null,
  slug: string | undefined,
  page: number,
  pageSize: number,
  containsFilters?: Partial<Record<string, string>>,
  entryOverride?: MasterCrudRegistryEntry,
) {
  const def = entryOverride ?? (slug ? MASTER_CRUD_REGISTRY[slug] : undefined);
  const filtersKey = masterEntityContainsFiltersKey(containsFilters);
  return useQuery({
    queryKey: queryKeys.masterEntityListPage(jwt, slug ?? null, page, pageSize, filtersKey),
    queryFn: () =>
      strapiListDynamicEntityPage(jwt!, def!.apiPlural, {
        populate: def!.populate,
        page,
        pageSize,
        containsFilters,
        exactFilters: def!.exactListFilters,
      }),
    enabled: Boolean(jwt && def),
    /** While page/size/filters change, avoid `data` going undefined — DataGrid uses `rowCount` and resets page if it hits 0 mid-fetch. */
    placeholderData: (previousData) => previousData,
  });
}

export function useMasterEntityMiniListQuery(jwt: string | null, apiPlural: string | undefined, enabled: boolean, populate?: string | undefined, pageSize: number = 100, skipTheRest: boolean = false) {
  return useQuery({
    queryKey: queryKeys.masterEntityMini(jwt, apiPlural ?? ''),
    queryFn: () => strapiListDynamicEntity(jwt!, apiPlural!, !!populate ? populate : '*', pageSize, skipTheRest),
    enabled: Boolean(jwt && apiPlural && enabled),
  });
}

export function useMasterEntityCreateMutation(
  jwt: string | null,
  slug: string | undefined,
  entryOverride?: MasterCrudRegistryEntry,
) {
  const qc = useQueryClient();
  const def = entryOverride ?? (slug ? MASTER_CRUD_REGISTRY[slug] : undefined);
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      strapiCreateDynamicEntity(jwt!, def!.apiPlural, data, def!.populate),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, slug ?? null) });
    },
  });
}

export function useMasterEntityUpdateMutation(
  jwt: string | null,
  slug: string | undefined,
  entryOverride?: MasterCrudRegistryEntry,
) {
  const qc = useQueryClient();
  const def = entryOverride ?? (slug ? MASTER_CRUD_REGISTRY[slug] : undefined);
  return useMutation({
    mutationFn: (args: { documentId: string; data: Record<string, unknown> }) =>
      strapiUpdateDynamicEntity(jwt!, def!.apiPlural, args.documentId, args.data, def!.populate),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, slug ?? null) });
    },
  });
}

export function useMasterEntityDeleteMutation(
  jwt: string | null,
  slug: string | undefined,
  entryOverride?: MasterCrudRegistryEntry,
) {
  const qc = useQueryClient();
  const def = entryOverride ?? (slug ? MASTER_CRUD_REGISTRY[slug] : undefined);
  return useMutation({
    mutationFn: (documentId: string) => strapiDeleteDynamicEntity(jwt!, def!.apiPlural, documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, slug ?? null) });
    },
  });
}
